/**
 * WaitlistBlock — Frente B Sessão 2 (reconciliação da agenda na UI).
 *
 * Resumo, NO painel da Conversa, das filas de espera em que ESTE cliente está.
 * Fecha a assimetria mapeada na AUDITORIA §6: quando a IA enfileira o cliente na
 * conversa, o operador não via (a fila só aparecia na /agenda, por recurso).
 *
 * Informacional (sem ações): a gestão da fila — aprovar/recusar/reordenar/liberar —
 * mora na /agenda (WaitlistPanel), por recurso. Aqui o operador só PRECISA SABER que
 * o cliente está na fila, de qual serviço e em que estado, para não retrabalhar.
 *
 * Mini-cards canônicos (Brief 2A r7b `.mini-card`), espelhando AppointmentBlock.
 */

import { Clock, Hourglass } from 'lucide-react';

import type { WaitlistSummaryFixture } from '../../hooks/use-panel-fixtures';

const STATUS_LABEL: Record<WaitlistSummaryFixture['status'], string> = {
  pending_approval: 'Aguardando liberação',
  active: 'Na fila de espera',
  offered: 'Vaga ofertada',
};

const STATUS_COLOR: Record<WaitlistSummaryFixture['status'], string> = {
  pending_approval: 'var(--warning-fg)',
  active: 'var(--text-secondary)',
  offered: 'var(--accent-fg)',
};

function formatOfferExpiry(iso: string): string {
  const fmt = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
  return fmt.format(new Date(iso));
}

export function WaitlistBlock({ items }: { items: WaitlistSummaryFixture[] }): JSX.Element {
  if (items.length === 0) {
    return (
      <div className="empty-mini">
        <Hourglass className="icon" size={14} aria-hidden />
        Não está em nenhuma fila de espera.
      </div>
    );
  }

  return (
    <>
      {items.map((w) => (
        <article key={w.id} className="mini-card" data-testid={`waitlist-${w.id}`}>
          <div className="mini-card__head">
            <Hourglass className="icon" size={14} aria-hidden />
            <span className="mini-card__title">{w.serviceName}</span>
            <span
              style={{
                marginLeft: 'auto',
                fontSize: 10,
                fontFamily: 'var(--font-mono)',
                color: STATUS_COLOR[w.status],
              }}
            >
              {STATUS_LABEL[w.status]}
            </span>
          </div>

          {w.status === 'offered' && w.offerExpiresAt !== null && (
            <div className="mini-card__meta" style={{ color: 'var(--accent-fg)' }}>
              <Clock className="icon" size={14} aria-hidden /> Oferta expira em{' '}
              {formatOfferExpiry(w.offerExpiresAt)}
            </div>
          )}
        </article>
      ))}
    </>
  );
}
